import type { BudgetsSigningFlow, FlowQuery, TaskQuery } from '../types/common'
import type { PartyWithContactRelations } from '../types/integrations/party/common'
import type { ContractState, SigningState } from '../types/states'
import { getSystemUserHeaders } from '../helpers/helper.flow'
import { getStakeholderInfo } from '../helpers/helper.parties'
import { type FlowTask, getFlows, getTasks, getTasksForFlows } from './flow.service'
import { getPartyByExternalId } from './party.service'
import { mapSigningSessionsQuery } from '../mappers/mapper.signing'
import { SIGN_SESSION_TASK_QUERY, BUDGET_SIGNING_TASK_QUERY } from '../types/signing'

export type ContractFlow = NonNullable<NonNullable<Awaited<ReturnType<typeof getFlows>>>['flows']>[number]

export type SignerSigningFlow = { flow: ContractFlow; isFailed: boolean }

type BudgetSigningTaskFilter = { signerId?: string; flowIds?: string[] }

const FLOW_ID_BATCH_SIZE = 50

export async function getPendingBudgetSigningTasks({
  signerId,
  flowIds,
}: BudgetSigningTaskFilter): Promise<FlowTask[]> {
  const query: TaskQuery = {
    ...BUDGET_SIGNING_TASK_QUERY,
    stripTaskContext: true,
    stripTaskVariables: true,
    stripTaskDefaults: true,
    ...(signerId && { data: { signerId } }),
  }
  if (flowIds) return await getTasksForFlows(flowIds, query, getSystemUserHeaders())

  const { tasks = [] } = (await getTasks(query, getSystemUserHeaders())) ?? {}

  return tasks
}

export async function isSignerForFlow(userId: string, flowId: string): Promise<boolean> {
  const tasks = await getPendingBudgetSigningTasks({ signerId: userId, flowIds: [flowId] })

  return tasks.length > 0
}

export async function getSigneeContact(userId: string): Promise<{ email?: string; phone?: string }> {
  try {
    const { data } = await getPartyByExternalId(userId, { 'Flow-Process-User': getSystemUserHeaders() })
    const { email, phone } = getStakeholderInfo(data as PartyWithContactRelations)

    return { email, phone }
  } catch (error) {
    if (/Response: 404/.test(String(error))) return {}
    throw error
  }
}

export function hasSigningFailed(flow: ContractFlow): boolean {
  const data = flow.data as unknown as SigningState

  return !!flow.hasErrors && !data?.session
}

export async function getFlowIdsWithPendingSigningTask(flowIds: string[]): Promise<Set<string>> {
  const tasks = await getTasksForFlows(
    flowIds,
    { ...SIGN_SESSION_TASK_QUERY, stripTaskContext: true, stripTaskVariables: true, stripTaskDefaults: true },
    getSystemUserHeaders(),
  )

  return new Set(tasks.map((task) => task.flowId))
}

export async function getSignerSigningFlows(userId: string): Promise<SignerSigningFlow[]> {
  const { flows = [] } = (await getFlows(mapSigningSessionsQuery(userId), getSystemUserHeaders())) ?? {}
  if (flows.length === 0) return []

  const flowIdsWithTask = await getFlowIdsWithPendingSigningTask(flows.map((flow) => flow.flowId))

  return flows.flatMap((flow): SignerSigningFlow[] => {
    if (flowIdsWithTask.has(flow.flowId)) return [{ flow, isFailed: false }]

    return hasSigningFailed(flow) ? [{ flow, isFailed: true }] : []
  })
}

export async function getBudgetIdsBeingSigned(userId: string): Promise<Set<string>> {
  const { flows = [] } = (await getFlows(mapSigningSessionsQuery(userId), getSystemUserHeaders())) ?? {}

  const budgets: BudgetsSigningFlow['budgets'] = flows.flatMap(
    (flow) => (flow.data as unknown as SigningState)?.metadata?.budgets ?? [],
  )

  return new Set(budgets.map((budget) => String(budget.id)))
}

export function isBudgetBeingSigned(flow: ContractFlow, budgetIdsBeingSigned: Set<string>): boolean {
  const budgetId = String((flow.data as unknown as ContractState)?.source?.Contract?.Id)

  return budgetIdsBeingSigned.has(budgetId)
}

async function getContractFlows(flowIds: string[]): Promise<ContractFlow[]> {
  const flows: ContractFlow[] = []
  for (let index = 0; index < flowIds.length; index += FLOW_ID_BATCH_SIZE) {
    const query: FlowQuery = {
      flowDefinitionId: 'contract',
      status: 'active',
      flowId: flowIds.slice(index, index + FLOW_ID_BATCH_SIZE),
    }
    const result = await getFlows(query, getSystemUserHeaders())
    flows.push(...(result?.flows ?? []))
  }

  return flows
}

export async function getContractFlowsReadyForSigning(userId: string): Promise<ContractFlow[]> {
  const [tasks, budgetIdsBeingSigned] = await Promise.all([
    getPendingBudgetSigningTasks({ signerId: userId }),
    getBudgetIdsBeingSigned(userId),
  ])

  const flowIds = [...new Set(tasks.map((task) => task.flowId))]
  if (flowIds.length === 0) return []

  const flows = await getContractFlows(flowIds)

  return flows.filter((flow) => !isBudgetBeingSigned(flow, budgetIdsBeingSigned))
}
